import { useReactiveVar } from '@apollo/client';
import { useBreakpointValue } from '@hover/blueprint';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getOrCreateSplitKey } from 'src/modules/app/components/Providers/Telemetry/Split/';
import { useHomeownerAppProfileQuery } from 'src/modules/homeownerApp/graphql/generated';
import { getReferrerHostname } from 'src/modules/homeownerApp/helpers';
import { useWebViewListener } from 'src/modules/homeownerApp/hooks/useWebViewListener';
import { useEmbedContext } from 'src/modules/homeownerApp/providers/EmbedProvider';
import { SetIntercomVisibility } from 'src/shared/components/SetIntercomVisibility';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import { designerAnonymousSessionVar } from 'src/shared/graphql/client/link/designerAnonymousSession';
import type { User } from 'src/shared/graphql/generated';
import {
  useCreateDesignerAnonymousSessionMutation,
  useGetScansQuery,
} from 'src/shared/graphql/generated';
import { useCurrentPage, useIsEmbed, useQueryParam } from 'src/shared/hooks';
import { useAuthExperience } from 'src/shared/hooks/useAuthExperience';
import { moduleViewed } from 'src/shared/telemetry';

import { getUserSession, useUserSession } from '../hooks/useUserSession';

export const HomeownerAppMenuNames = {
  ORIGINAL_IMAGE_MORE_ACTIONS: 'original_image_more_actions',
  PROFILE: 'profile',
  PROJECT_MORE_ACTIONS: 'project_more_actions',
  PROJECT_SELECTION: 'project_selection',
  MATERIAL_SELECTION: 'material_selection',
  SIGN_IN: 'sign_in',
  UPLOAD_PHOTO: 'upload_photo',
  EDIT_PROJECT: 'edit_project',
  SHARE_PROJECT: 'share_project',
  IMAGE_COLLECTION: 'image_collection',
  START_SCAN_LEARN_MORE: 'start_scan_learn_more',
  DOWNLOAD_APP: 'download_app',
  DOWNLOAD_APP_SHEET: 'download_app_sheet',
  PROJECT_PAYMENT_GATE: 'project_payment_gate',
} as const;

const homeownerAppMenuTrackIds = {
  [HomeownerAppMenuNames.ORIGINAL_IMAGE_MORE_ACTIONS]:
    'original-image-more-bottom-sheet',
  [HomeownerAppMenuNames.PROFILE]: 'profile-bottom-sheet',
  [HomeownerAppMenuNames.PROJECT_MORE_ACTIONS]:
    'project-more-actions-bottom-sheet',
  [HomeownerAppMenuNames.PROJECT_SELECTION]: 'project-selection-bottom-sheet',
  [HomeownerAppMenuNames.MATERIAL_SELECTION]: 'material-selection-bottom-sheet',
  [HomeownerAppMenuNames.SIGN_IN]: 'log-in-sign-up-bottom-sheet',
  [HomeownerAppMenuNames.UPLOAD_PHOTO]: 'add-photo-bottom-sheet',
  [HomeownerAppMenuNames.EDIT_PROJECT]: 'edit-project-bottom-sheet',
  [HomeownerAppMenuNames.SHARE_PROJECT]: 'share-project-bottom-sheet',
  [HomeownerAppMenuNames.IMAGE_COLLECTION]: 'image_collection',
  [HomeownerAppMenuNames.START_SCAN_LEARN_MORE]: 'start_scan_learn_more',
  [HomeownerAppMenuNames.DOWNLOAD_APP]: 'download_app',
  [HomeownerAppMenuNames.DOWNLOAD_APP_SHEET]: 'download_app_sheet',
  [HomeownerAppMenuNames.PROJECT_PAYMENT_GATE]: 'project_payment_gate',
};

export type HomeownerAppMenuNamesValues =
  (typeof HomeownerAppMenuNames)[keyof typeof HomeownerAppMenuNames];
export interface HomeownerAppTrackingData {
  device_type: string;
  embedded_at?: string;
  embed_tracking_id?: string;
  embed_org_id?: number;
  feature: FeatureTracking;
  org_id?: number;
  page_or_screen_name: string;
  user_id?: number;
  user_authenticated?: boolean;
  session_id?: string;
  split_key: string;
}

export interface HomeownerAppContextProps {
  loggedIn: boolean;
  user?: Partial<User>;
  imageFileToUpload?: File;
  setImageFileToUpload: (file?: File) => void;
  currentMenu: HomeownerAppMenuNamesValues | null;
  setCurrentMenu: (menu: HomeownerAppMenuNamesValues | null) => void;
  trackingData: HomeownerAppTrackingData;
  disableAddProjectNavigation: boolean;
  setDisableAddProjectNavigation: (disable: boolean) => void;
}

export const initialContext = {
  loggedIn: false,
  user: undefined,
  currentMenu: null,
  setImageFileToUpload: () => null,
  setCurrentMenu: () => null,
  trackingData: {
    device_type: '',
    feature: FeatureTracking.HOMEOWNER_APP,
    page_or_screen_name: '',
    split_key: '',
  },
  disableAddProjectNavigation: false,
  setDisableAddProjectNavigation: () => null,
};

type CurrentMenu = HomeownerAppMenuNamesValues | null;

const homeownerAppContext =
  createContext<HomeownerAppContextProps>(initialContext);

const useAnonymousDesignerSession = (currentUser: User | undefined) => {
  const isEmbed = useIsEmbed();

  const [createAnonymousSession] = useCreateDesignerAnonymousSessionMutation();
  const designerAnonymousSession = useReactiveVar(designerAnonymousSessionVar);

  useEffect(() => {
    // Do not allow currentUser and designerAnonymousSessionVar simultaneously
    if (currentUser && designerAnonymousSession) {
      designerAnonymousSessionVar('');
    }

    if (!currentUser && isEmbed && !designerAnonymousSession) {
      void (async () => {
        const response = await createAnonymousSession();
        const jwt = response.data?.createDesignerAnonymousSession.jwt;
        if (jwt) {
          designerAnonymousSessionVar(jwt);
        }
      })();
    }
  }, [currentUser, isEmbed, designerAnonymousSession, createAnonymousSession]);
};

const useEmbedTrackingId = () => {
  const embedTrackingIdParam = useQueryParam('embedTrackingId');
  // By using default state we ensure that once the embedTrackingId is set, it
  // can't be changed or removed if the query param is changed or removed
  const [embedTrackingId] = useState(
    typeof embedTrackingIdParam === 'string' ? embedTrackingIdParam : '',
  );

  return embedTrackingId;
};

const HomeownerAppProvider = ({
  trackingDataOverrides,
  children,
  displayIntercom = false,
}: React.PropsWithChildren<{
  trackingDataOverrides?: Partial<HomeownerAppTrackingData>;
  displayIntercom?: boolean;
}>) => {
  // The useAuthExperience hook runs a useEffect that sets the user for
  // Split and Segment that gets run on the client.  Do not need to query
  // the full profile on server since data does not get consumed until client.
  useAuthExperience({ ssr: false });
  useWebViewListener();
  const [currentMenu, setCurrentMenuState] = useState<CurrentMenu>(null);
  const [imageFileToUpload, setImageFileToUpload] = useState<
    File | undefined
  >();
  const [disableAddProjectNavigation, setDisableAddProjectNavigation] =
    useState(false);
  const { isEmbed, embedOrgId, embeddedAt } = useEmbedContext();
  const embedTrackingId = useEmbedTrackingId();
  const embeddedAtParam = isEmbed
    ? embeddedAt || getReferrerHostname()
    : undefined;

  const { data: profileData } = useHomeownerAppProfileQuery({
    errorPolicy: 'all',
    onCompleted: data => {
      if (data?.profile?.user?.orgs?.[0].hasReachedDesignerProjectCap) {
        sessionStorage.setItem('showPaywallModal', 'true');
      }
    },
  });

  const currentUser = profileData?.profile?.user as User;
  const userSessionId = useUserSession({
    userId: currentUser?.id,
    embeddedAt: embeddedAtParam,
  });

  useAnonymousDesignerSession(currentUser);

  const mobileOrDesktop = useBreakpointValue({
    base: 'mobile',
    tablet: 'tablet',
    desktop: 'desktop',
  });
  const showIntercom = displayIntercom && mobileOrDesktop === 'desktop';

  const pageName = useCurrentPage() || '';

  // Fetches scans for a logged in user
  // This is a job query and can be quite slow (one of Hyperion's slowest)
  // We don't need this query for the initial load of the page
  useGetScansQuery({ skip: !currentUser || isEmbed, ssr: false });

  // Sometimes users with expired sessions can trigger some
  // events before the user session is renewed, so we need
  // to check if the stored session is reliable for tracking.
  const isSessionReliable = useCallback(
    () =>
      !userSessionId
        ? false
        : getUserSession()?.expiresAt! > new Date().toISOString(),
    [userSessionId],
  );

  const trackingData = useMemo(
    (): HomeownerAppTrackingData => ({
      device_type: mobileOrDesktop,
      embedded_at: embeddedAtParam,
      embed_tracking_id:
        isEmbed && embedTrackingId ? embedTrackingId : undefined,
      feature: isEmbed
        ? FeatureTracking.GEN_AI_EMBED
        : FeatureTracking.HOMEOWNER_APP,
      user_id: currentUser?.id,
      org_id: currentUser?.orgs?.[0]?.id,
      page_or_screen_name: pageName,
      user_authenticated: !!currentUser,
      session_id: isSessionReliable() ? userSessionId : undefined,
      split_key: typeof window !== 'undefined' ? getOrCreateSplitKey() : '',
      embed_org_id: isEmbed && embedOrgId ? embedOrgId : undefined,
      ...trackingDataOverrides,
    }),
    [
      currentUser,
      embedTrackingId,
      embeddedAtParam,
      isEmbed,
      embedOrgId,
      isSessionReliable,
      mobileOrDesktop,
      pageName,
      userSessionId,
      trackingDataOverrides,
    ],
  );

  const setCurrentMenu = useCallback(
    (menu: CurrentMenu) => {
      if (menu) {
        moduleViewed({
          ...trackingData,
          track_id: homeownerAppMenuTrackIds[menu],
        });
      }
      setCurrentMenuState(menu);
    },
    [trackingData],
  );

  const value = useMemo(
    () => ({
      user: currentUser,
      loggedIn: !!currentUser,
      imageFileToUpload,
      setImageFileToUpload,
      currentMenu,
      setCurrentMenu,
      trackingData,
      isEmbed,
      disableAddProjectNavigation,
      setDisableAddProjectNavigation,
    }),
    [
      currentMenu,
      currentUser,
      imageFileToUpload,
      setCurrentMenu,
      trackingData,
      isEmbed,
      disableAddProjectNavigation,
      setDisableAddProjectNavigation,
    ],
  );

  return (
    <homeownerAppContext.Provider value={value}>
      <SetIntercomVisibility visible={showIntercom} />
      {children}
    </homeownerAppContext.Provider>
  );
};

export { HomeownerAppProvider, homeownerAppContext };
